import { Location } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideRouter } from '@angular/router';
import { PureAbility } from '@casl/ability';
import { AbilityServiceSignal } from '@casl/angular';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthenticationService } from '@shared/authentication.service';
import { Ad6Constants } from '@shared/constants';
import { eventInterceptor } from '@shared/event.interceptor';
import { buildSignalAbility } from '@shared/permissions/ability';
import { AppAbility } from '@shared/permissions/types';
import { tokenInterceptor } from '@shared/token.interceptor';
import { Observable } from 'rxjs';

import { routes } from './app.routes';

const initializeAuthenticationService = (authenticationService: AuthenticationService, location: Location) => {
  return async (): Promise<void> => await authenticationService.init(location.path());
};

const appInit = (translateService: TranslateService) => (): Observable<unknown> =>
  translateService.use(Ad6Constants.DEFAULT_LANGUAGE);

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      const authenticationInitializerFn = initializeAuthenticationService(
        inject(AuthenticationService),
        inject(Location),
      );

      return authenticationInitializerFn();
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([tokenInterceptor, eventInterceptor])),
    provideTranslateService({
      fallbackLang: 'nl',
      loader: provideTranslateHttpLoader({
        prefix: './i18n/',
        suffix: '.json',
      }),
    }),
    provideAppInitializer(() => {
      const initializerFn = appInit(inject(TranslateService));

      return initializerFn();
    }),
    [{ provide: MAT_DATE_LOCALE, useValue: 'nl-NL' }],

    {
      provide: PureAbility,
      useFactory: (): AppAbility => {
        const authService = inject(AuthenticationService);
        return buildSignalAbility(authService.getUser);
      },
    },
    // Koppel de geüpdatete AppAbility aan de CASL Signal Service
    { provide: AbilityServiceSignal, useClass: AbilityServiceSignal },
  ],
};
